import React, { useEffect, useState } from 'react';
import { BookOpen, Plus, Edit, Trash, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addBlog,
  editBlog,
  deleteBlog,
  getSingleBlog,
  clearBlogState,
  fetchBlogs,
} from '../redux/BlogSlice';
import BlogForm from '../components/BlogForm';
import { Link } from 'react-router-dom';

const Blogs = () => {
  const dispatch = useDispatch();
  const { blogs, singleBlog, loading, error, success } = useSelector((state) => state.blogs);

  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // 🧭 Show notification for success/error
  useEffect(() => {
    if (success) {
      showNotification('success', success);
      dispatch(clearBlogState());
    }
    if (error) {
      showNotification('error', error);
      dispatch(clearBlogState());
    }
  }, [success, error, dispatch]);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  const handleAddNew = () => {
    setEditingBlog(null);
    setShowModal(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setShowModal(true);
  };

  const handleFormSubmit = async (formData) => {
    if (editingBlog) {
      await dispatch(editBlog({ id: editingBlog.id, blogData: formData }));
    } else {
      await dispatch(addBlog(formData));
    }
    setShowModal(false);
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    const id = blogToDelete.id
    await dispatch(deleteBlog({id}));
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBlogToDelete(null);
  };

  // 🎯 Fetch blogs on component mount
  useEffect(() => {
    dispatch(fetchBlogs());
  }, [dispatch]);


  return (
    <div className="p-6">
      {/* Notification */}
      {notification.show && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center justify-between ${notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
            }`}
        >
          <div className="flex items-center space-x-3">
            {notification.type === 'success' ? (
              <CheckCircle className="text-green-600" size={20} />
            ) : (
              <AlertCircle className="text-red-600" size={20} />
            )}
            <span
              className={
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }
            >
              {notification.message}
            </span>
          </div>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Blog Management</h1>
          <p className="text-gray-500 text-sm">Create and manage your blog posts</p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Blog Post</span>
        </button>
      </div>

      {/* Blog Grid */}
      {loading ? (
        <div className="text-center text-gray-500 py-10">Loading blogs...</div>
      ) : blogs?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div key={blog?.id} className="group bg-white border border-gray-200 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src={blog?.image || 'https://via.placeholder.com/400'}
                  alt={blog?.title}
                  className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity duration-500"></div>

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 space-x-3">
                  <button
                    onClick={() => handleEdit(blog)}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors"
                    title="Edit Blog"
                  >
                    <Edit size={20} className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(blog)}
                    className="p-3 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
                    title="Delete Blog"
                  >
                    <Trash size={20} className="text-red-600" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <p className="text-xs text-gray-500 mb-3 tracking-wide">{blog?.created_at ? new Date(blog.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }) : ""}</p>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight uppercase tracking-wide">
                  {blog.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4 uppercase tracking-wide">{blog?.author}</p>
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{blog?.excerpt}</p>
                <Link
                  to={`/dashboard/blog/${blog?.id}`}
                >
                  <button className="text-xs text-gray-900 border border-gray-900 px-4 py-2 hover:bg-gray-900 hover:text-white transition-colors uppercase tracking-wider">
                    Read More
                  </button>
                </Link>

              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No blog posts yet</h3>
          <p className="text-gray-500 mb-4">Create your first blog post to get started</p>
          <button
            onClick={handleAddNew}
            className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
          >
            Add Your First Blog Post
          </button>
        </div>
      )}

      {/* Blog Form Modal */}
      <BlogForm
        showModal={showModal}
        setShowModal={setShowModal}
        editingBlog={editingBlog}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Blog Post</h3>
              </div>
            </div>

            <p className="text-gray-600 mb-2">
              Are you sure you want to delete <strong>"{blogToDelete?.title}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blogs;
